import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ExternalLink, ShieldCheck } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm'
import { Badge, StatusDot } from '@/components/ui/display'
import {
  completeTeamsTranscriptSetup,
  connectMicrosoftTenant,
  disconnectMicrosoftTenant,
  type MicrosoftTeamsSettings,
  type OrgSettings,
} from '@/data/settings'
import { Consequence, SettingsCard } from './SettingsCard'

const TEAMS_ADMIN_URL = 'https://admin.teams.microsoft.com/meetings/settings'

/**
 * External systems.
 *
 * Only the ones an administrator can actually change from here. The model,
 * transcription and Notion credentials are deployment configuration — they come
 * from the environment, and a field that pretends otherwise is a field whose
 * value is silently ignored.
 *
 * The Microsoft connection is not part of the settings draft and has no place
 * in the save bar: it is changed by a consent round trip with Microsoft, so its
 * buttons act immediately and the card reads the server's copy rather than the
 * draft.
 */
export function IntegrationsSettings({ server }: { server: OrgSettings }) {
  return <MicrosoftTeamsCard teams={server.integrations.microsoftTeams} />
}

/**
 * The Teams connection, as a two-step state rather than a switch.
 *
 * Connecting the tenant and enabling transcript access are separate grants made
 * by different people in different places, and a card that collapses them into
 * one "connected" badge is the reason nobody notices speaker attribution is
 * still off — which is what turns every name in a transcript into "Speaker 2".
 */
function MicrosoftTeamsCard({ teams }: { teams: MicrosoftTeamsSettings }) {
  const queryClient = useQueryClient()
  const confirm = useConfirm()

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['org-settings'] })

  const connect = useMutation({ mutationFn: connectMicrosoftTenant, onSuccess: invalidate })
  const disconnect = useMutation({ mutationFn: disconnectMicrosoftTenant, onSuccess: invalidate })
  const enable = useMutation({ mutationFn: completeTeamsTranscriptSetup, onSuccess: invalidate })

  const busy = connect.isPending || disconnect.isPending || enable.isPending
  const ready = teams.connected && teams.transcriptAccess && teams.speakerAttribution

  return (
    <SettingsCard
      title="Microsoft Teams"
      actions={
        teams.connected ? (
          <Badge tone={ready ? 'success' : 'warning'}>
            <StatusDot tone={ready ? 'success' : 'warning'} />
            {ready ? 'Ready' : 'Setup incomplete'}
          </Badge>
        ) : (
          <Badge tone="neutral">Not connected</Badge>
        )
      }
    >
      {teams.connected ? (
        <>
          <div className="flex items-center gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] px-3 py-2.5">
            <ShieldCheck className="size-4 shrink-0 text-[var(--color-success)]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-[var(--color-fg)]">
                {teams.tenantName}
              </span>
              <span className="block text-2xs text-[var(--color-fg-subtle)]">
                Recording and transcript permissions granted for the organisation.
              </span>
            </span>
            <Button
              size="xs"
              variant="ghost"
              disabled={busy}
              className="shrink-0 hover:text-[var(--color-danger)]"
              onClick={async () => {
                const ok = await confirm({
                  title: 'Disconnect the Microsoft tenant?',
                  body: 'Scheduled meetings stop being recorded until it is connected again. Meetings already recorded keep their transcripts and notes.',
                  confirmLabel: 'Disconnect',
                  tone: 'danger',
                })
                if (ok) disconnect.mutate()
              }}
            >
              Disconnect
            </Button>
          </div>

          <div className="divide-y divide-[var(--color-border-subtle)] rounded-[var(--radius-md)] border border-[var(--color-border)]">
            <SetupRow
              done={teams.transcriptAccess}
              label="Graph transcript access"
              hint="Lets Meeting Notes read the transcript Teams produces."
            />
            <SetupRow
              done={teams.speakerAttribution}
              label="Speaker attribution"
              hint="Without it every line is attributed to “Speaker 1”, and mapping names afterwards is guesswork."
            />
          </div>

          {ready ? null : (
            <div className="flex flex-wrap items-center gap-2">
              {/* An anchor, styled as the button — not a button that navigates,
                  so it can be opened in a new tab the ordinary way. */}
              <a
                href={TEAMS_ADMIN_URL}
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              >
                Open Teams admin centre
                <ExternalLink className="size-3" />
              </a>
              <Button
                size="sm"
                variant="primary"
                loading={enable.isPending}
                disabled={busy}
                onClick={() => enable.mutate()}
              >
                I enabled both
              </Button>
              <span className="text-xs text-[var(--color-fg-subtle)]">
                Meetings → Meeting settings. One tenant-wide change.
              </span>
            </div>
          )}
        </>
      ) : (
        <>
          <Consequence>
            A Microsoft administrator grants the recording and transcript
            permissions once for the whole organisation. No Teams app is
            installed, and nothing is recorded until someone starts a recording.
          </Consequence>
          <Button
            size="sm"
            variant="primary"
            loading={connect.isPending}
            onClick={() => connect.mutate()}
          >
            <ShieldCheck className="size-3.5" />
            Connect Microsoft tenant
          </Button>
        </>
      )}
    </SettingsCard>
  )
}

function SetupRow({
  done,
  label,
  hint,
}: {
  done: boolean
  label: string
  hint: string
}) {
  return (
    <div className="flex items-start gap-2.5 px-3 py-2">
      <span
        className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-[var(--radius-xs)] border"
        style={
          done
            ? { borderColor: 'var(--color-success)', background: 'var(--color-success)' }
            : { borderColor: 'var(--color-border-strong)' }
        }
        aria-hidden
      >
        {done ? <Check className="size-3 text-white" /> : null}
      </span>
      <span className="min-w-0">
        <span className="block text-xs text-[var(--color-fg)]">{label}</span>
        <span className="mt-0.5 block text-2xs text-[var(--color-fg-subtle)]">{hint}</span>
      </span>
      {done ? null : (
        <Badge tone="warning" className="ml-auto shrink-0">
          Off
        </Badge>
      )}
    </div>
  )
}
